import { memo } from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

type MarkdownTextProps = {
  text: string
  className?: string
}

const MarkdownTextComponent = ({ text, className }: MarkdownTextProps) => {
  const components: Components = {
    // Headers
    h1: ({ children, ...props }) => (
      <h1 className="tw-text-3xl tw-font-bold tw-mt-6 tw-mb-4" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className="tw-text-2xl tw-font-bold tw-mt-5 tw-mb-3" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="tw-text-xl tw-font-bold tw-mt-4 tw-mb-2" {...props}>
        {children}
      </h3>
    ),

    // Lists
    ul: ({ children, ...props }) => <ul {...props}>{children}</ul>,
    ol: ({ children, ...props }) => <ol {...props}>{children}</ol>,
    li: ({ children, ...props }) => <li {...props}>{children}</li>,

    // Paragraphs and text
    p: ({ children, ...props }) => <p {...props}>{children}</p>,
    strong: ({ children, ...props }) => (
      <strong className="tw-font-bold" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em className="tw-italic" {...props}>
        {children}
      </em>
    ),

    // Code blocks
    code: ({ children, ...props }) => {
      return (
        <code className="block bg-gray-100 rounded p-3 font-mono text-sm overflow-x-auto my-2" {...props}>
          {children}
        </code>
      )
    },

    // Links
    a: ({ children, ...props }) => (
      <a className="text-blue-600 hover:underline" {...props}>
        {children}
      </a>
    ),

    // Blockquotes
    blockquote: ({ children, ...props }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic" {...props}>
        {children}
      </blockquote>
    ),

    // Tables
    table: ({ children, ...props }) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border-collapse border border-gray-300" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-gray-50" {...props}>
        {children}
      </thead>
    ),
    tr: ({ children, ...props }) => (
      <tr className="border-b border-gray-300" {...props}>
        {children}
      </tr>
    ),
    th: ({ children, ...props }) => (
      <th className="border-r border-gray-300 px-4 py-2 text-left" {...props}>
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="border-r border-gray-300 px-4 py-2" {...props}>
        {children}
      </td>
    ),
  }

  return (
    <div className={`prose max-w-none ${className || ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  )
}

export const MarkdownText = memo(MarkdownTextComponent)
